import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmDatePickerModal } from '../components/NmDatePickerModal';
import { calendarActions } from '../services/api';
import { isoToJalaliKey, jalaliKeyToIso } from '../utils/jalali';
import type { CalendarActionTarget } from '../features/calendar/CalendarItemActionSheet';

interface Options {
  userId?: number | null;
  onReload?: () => void | Promise<void>;
  onMoved?: (targetDateIso: string) => void | Promise<void>;
  showToast: (message: string, variant?: 'success' | 'error' | 'info') => void;
}

/** Date-picker move flow for calendar metrics, health reports, events, and notes. */
export function useCalendarItemMove({ userId, onReload, onMoved, showToast }: Options) {
  const { t } = useTranslation();
  const [moveTarget, setMoveTarget] = useState<CalendarActionTarget | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const requestMove = useCallback((target: CalendarActionTarget) => {
    setMoveTarget(target);
    setPickerOpen(true);
  }, []);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setMoveTarget(null);
  }, []);

  const moveItemToDate = useCallback(
    async (target: CalendarActionTarget, targetIso: string) => {
      if (!userId) return false;

      const sourceIso =
        target.kind === 'metric' ||
        target.kind === 'health_document' ||
        target.kind === 'event' ||
        target.kind === 'note' ||
        target.kind === 'workout'
          ? target.dateParam
          : undefined;

      if (sourceIso && sourceIso === targetIso) return false;

      try {
        if (target.kind === 'metric') {
          await calendarActions.cutPasteMetrics(userId, target.uniqueCode, targetIso);
        } else if (target.kind === 'health_document') {
          await calendarActions.cutPasteHealthDocument(userId, target.id, targetIso);
        } else if (target.kind === 'event') {
          await calendarActions.cutPasteEvent(userId, target.id, targetIso);
        } else if (target.kind === 'note') {
          await calendarActions.cutPasteNote(userId, target.id, targetIso);
        } else if (target.kind === 'workout') {
          await calendarActions.cutPasteWorkout(userId, target.id, targetIso);
        } else {
          return false;
        }

        showToast(
          t('calendar.toast.itemMoved', {
            date: isoToJalaliKey(targetIso) || targetIso,
            defaultValue: `Moved to ${targetIso}`,
          }),
          'success',
        );
        await onReload?.();
        await onMoved?.(targetIso);
        return true;
      } catch {
        showToast(t('calendar.toast.itemMoveFailed', 'Failed to move item'), 'error');
        return false;
      }
    },
    [onMoved, onReload, showToast, t, userId],
  );

  const confirmMove = useCallback(
    async (jalaliKey: string) => {
      if (!moveTarget || !userId) return;

      const isoDate = jalaliKeyToIso(jalaliKey);
      if (!isoDate) return;

      await moveItemToDate(moveTarget, isoDate);
      closePicker();
    },
    [closePicker, moveItemToDate, moveTarget, userId],
  );

  const initialPickerValue =
    moveTarget &&
    (moveTarget.kind === 'metric' ||
      moveTarget.kind === 'health_document' ||
      moveTarget.kind === 'event' ||
      moveTarget.kind === 'note' ||
      moveTarget.kind === 'workout')
      ? isoToJalaliKey(moveTarget.dateParam)
      : '';

  const movePicker = (
    <NmDatePickerModal
      open={pickerOpen}
      value={initialPickerValue || ''}
      onClose={closePicker}
      onConfirm={confirmMove}
    />
  );

  return { requestMove, movePicker, moveItemToDate };
}
